import { useEffect } from "react"

import {
  getCustomerMajorityOwnerListQueryKey,
  prefetchCustomerCompanyRetrieveQuery,
  prefetchCustomerMajorityOwnerRetrieveQuery,
  prefetchCustomerPartialMajorityOwnerRetrieveQuery,
  useCustomerCompanyRetrieveSuspense,
  useCustomerMajorityOwnerCreate,
  useCustomerMajorityOwnerList,
  useCustomerMajorityOwnerPartialUpdate,
  useCustomerMajorityOwnerRetrieve,
  useCustomerPartialMajorityOwnerPartialUpdate,
  useCustomerPartialMajorityOwnerRetrieve,
} from "@boundless/bl-api-client/customer/services"
import { CustomerPartialMajorityOwnerPartialUpdateBody } from "@boundless/bl-api-client/customer/services.zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { useForm } from "react-hook-form"
import { z, type ZodType } from "zod"

import { OwnershipDetailsForm } from "@/components/ownership/OwnershipDetailsForm"
import { Form } from "@/components/ui/form"
import type { ErrorType } from "@/lib/clients"
import { OwnerIndex } from "@/lib/constants"
import { ownershipDetailsErrorMap } from "@/lib/errors"
import { CompanyFlowContext } from "@/lib/flows/company"
import { useAutosaveDraft } from "@/lib/forms/useAutosaveDraft"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"
import { ownershipDetailsFormSchema as formSchema } from "@/lib/schemas"
import { shouldRetryFactory } from "@/lib/utils"

// An owner slot nobody has filled in yet has no record, so a 404 is expected
// rather than a failure. Server errors are still retried, unlike the default.
const retryOwnerQuery = shouldRetryFactory({
  maxRetries: 2,
  skipServerError: false,
  skippedStatuses: [404, 429],
})

export const Route = createFileRoute("/_dashboard/company/ownership-details")({
  component: OwnershipDetailsView,
  validateSearch: (search: Record<string, string>) => {
    return {
      step: (search.step as "additional" | "primary") ?? "primary",
    }
  },
  beforeLoad: ({ context: { store, hasPermissions }, search }) => {
    if (!hasPermissions(["customer.view_majorityowner"])) {
      throw redirect({ to: "/" })
    }
    const companyUuid = store.getState().activeCompanyUuid
    if (!companyUuid) {
      throw redirect({ to: "/home" })
    }

    // For the loader below only. Moving between the two owners changes just
    // the search, which does not re-run this, so anything derived from `step`
    // here is stale by the time the other owner is on screen. The component
    // reads the step itself.
    const ownerIndex =
      search.step === "additional" ? OwnerIndex.SECOND : OwnerIndex.FIRST

    return { companyUuid, ownerIndex }
  },
  loader: async ({ context: { companyUuid, ownerIndex, queryClient } }) => {
    await prefetchCustomerCompanyRetrieveQuery(queryClient, companyUuid)
    await prefetchCustomerMajorityOwnerRetrieveQuery(
      queryClient,
      companyUuid,
      ownerIndex,
      { query: { retry: retryOwnerQuery } },
    )
    // A slot nobody has typed into has no draft, so a 404 is the ordinary
    // case here rather than a failure, exactly as for the owner above.
    await prefetchCustomerPartialMajorityOwnerRetrieveQuery(
      queryClient,
      companyUuid,
      ownerIndex,
      { query: { retry: retryOwnerQuery } },
    )
  },
})

function OwnershipDetailsView() {
  // Read from the search rather than the route context. Moving between the two
  // owners changes only the search, which does not re-run `beforeLoad`, so the
  // context still describes the owner the borrower came from.
  const { step } = Route.useSearch()
  const isAdditionalOwnerStep = step === "additional"
  const ownerIndex = isAdditionalOwnerStep
    ? OwnerIndex.SECOND
    : OwnerIndex.FIRST

  // Both owners share this route, so moving between them does not remount on
  // its own. Without a fresh mount the form keeps the previous owner's typed
  // values, which `keepDirtyValues` reads as the borrower's unsaved work, and
  // autosave would file owner 1's answers under owner 2.
  return (
    <OwnershipSlot
      key={ownerIndex}
      ownerIndex={ownerIndex}
      isAdditionalOwnerStep={isAdditionalOwnerStep}
    />
  )
}

interface SlotProps {
  ownerIndex: number
  isAdditionalOwnerStep: boolean
}

function OwnershipSlot({ ownerIndex, isAdditionalOwnerStep }: SlotProps) {
  const queryClient = useQueryClient()
  const { companyUuid } = Route.useRouteContext()

  const flowActorRef = CompanyFlowContext.useActorRef()

  const goBack = () => flowActorRef.send({ type: "back" })
  const goNext = () => flowActorRef.send({ type: "next" })
  const setHasMultipleOwners = (hasMultipleOwners: boolean) =>
    flowActorRef.send({ type: "setHasMultipleOwners", hasMultipleOwners })

  // We use `useSelector` to get some specific state from the CompanyFlowContext
  // actor, and avoiding re-rendering the component on every state change (it
  // only re-renders when the result of the given selector changes).
  const hasMultipleOwners = CompanyFlowContext.useSelector(
    ({ context: { hasMultipleOwners } }) => hasMultipleOwners,
  )

  const { data: companyData, queryKey: customerCompanyRetrieveQueryKey } =
    useCustomerCompanyRetrieveSuspense(companyUuid)

  const {
    data: ownershipData,
    isLoading: isMajorityOwnerRetrieveLoading,
    queryKey: majorityOwnerQueryKey,
  } = useCustomerMajorityOwnerRetrieve(companyUuid, ownerIndex, {
    query: { staleTime: 0, refetchOnWindowFocus: false },
  })

  const { data: allOwners } = useCustomerMajorityOwnerList(companyUuid, {
    query: { enabled: isAdditionalOwnerStep, refetchOnWindowFocus: false },
  })

  // A slot nobody has typed into has no draft, so a 404 here is ordinary.
  const { data: ownerDraft, isLoading: isOwnerDraftLoading } =
    useCustomerPartialMajorityOwnerRetrieve(companyUuid, ownerIndex, {
      query: {
        staleTime: 0,
        refetchOnWindowFocus: false,
        retry: retryOwnerQuery,
      },
    })

  const {
    mutate: createOwnershipDetails,
    isPending: isCreatingOwnershipDetails,
  } = useCustomerMajorityOwnerCreate()
  const {
    mutate: updateOwnershipDetails,
    isPending: isUpdatingOwnershipDetails,
  } = useCustomerMajorityOwnerPartialUpdate()

  const handleSubmit = async (data: z.infer<typeof formSchema>) => {
    // Saving the real owner deletes this slot's draft. A save already sent
    // would land after that and recreate one for a completed screen.
    await autosave.cancel()

    const finalData = {
      ...data,
      ownerIndex: ownerIndex,
    }

    if (ownershipData) {
      updateOwnershipDetails(
        {
          companyUuid,
          ownerIndex: ownerIndex,
          data: finalData,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: majorityOwnerQueryKey,
            })
            queryClient.invalidateQueries({
              queryKey: customerCompanyRetrieveQueryKey,
            })
            queryClient.invalidateQueries({
              queryKey: getCustomerMajorityOwnerListQueryKey(companyUuid),
            })
            goNext()
          },
          onError: (error) => {
            applyErrorsToForm(
              form.setError,
              error as ErrorType<ErrorResponseType>,
            )
          },
        },
      )
    } else {
      createOwnershipDetails(
        { companyUuid, data: finalData },
        {
          onSuccess: async () => {
            await queryClient.invalidateQueries({
              queryKey: customerCompanyRetrieveQueryKey,
            })
            await queryClient.invalidateQueries({
              queryKey: getCustomerMajorityOwnerListQueryKey(companyUuid),
            })
            goNext()
          },
          onError: (error) => {
            applyErrorsToForm(
              form.setError,
              error as ErrorType<ErrorResponseType>,
            )
          },
        },
      )
    }
  }

  const initialValues = {
    address: "",
    city: "",
    creditScore: 0,
    dateOfBirth: "",
    email: "",
    firstName: "",
    isForeignCitizen: false,
    lastName: "",
    ownershipPercentage: 0,
    phone: "",
    ssn: "",
    zipCode: "",
  }

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema as ZodType<z.infer<typeof formSchema>>, {
      errorMap: ownershipDetailsErrorMap,
    }),
    resetOptions: { keepDirtyValues: true },
    defaultValues: initialValues,
  })

  // Busy while submitting waits out a draft save, so it cannot submit twice.
  const isSubmitting =
    isCreatingOwnershipDetails ||
    isUpdatingOwnershipDetails ||
    form.formState.isSubmitting

  // No cache write on success, unlike the other screens. This route hydrates
  // from an effect watching the draft, so writing each save back into that
  // cache would refill the form as the borrower types.
  const { mutateAsync: updateDraft } =
    useCustomerPartialMajorityOwnerPartialUpdate({
      mutation: { meta: { shouldDisplayErrorToast: false } },
    })

  const autosave = useAutosaveDraft({
    form,
    initialValues,
    schema: CustomerPartialMajorityOwnerPartialUpdateBody,
    enabled: !ownershipData,
    // The slot is the one this component was mounted for. Sending the wrong
    // one would file this owner's answers under the other, and would surface
    // much later as the wrong prefill rather than as an error.
    save: (data) => updateDraft({ companyUuid, ownerIndex, data }),
  })

  const maxOwnershipPercentage = (() => {
    if (!isAdditionalOwnerStep) {
      return 100
    }

    const primaryOwnerOwnership = allOwners?.[0]?.ownershipPercentage ?? 0
    return 100 - primaryOwnerOwnership
  })()

  // Real record first, then whatever autosave kept, then empty — the same
  // cascade the other onboarding screens use.
  const source = ownershipData ?? ownerDraft

  // Only the fields the borrower has not touched are replaced, per
  // `keepDirtyValues`.
  const { hydrate } = autosave
  useEffect(() => {
    hydrate({
      address: source?.address || "",
      // A draft saved before one of these was answered holds a blank, which
      // the form's enums will not take. A real record cannot be blank.
      citizenship: source?.citizenship || undefined,
      city: source?.city || "",
      country: source?.country || undefined,
      // The draft column is nullable; the form field is not.
      creditScore: source?.creditScore ?? undefined,
      dateOfBirth: source?.dateOfBirth || "",
      email: source?.email || "",
      firstName: source?.firstName || "",
      isForeignCitizen: source?.isForeignCitizen ?? undefined,
      lastName: source?.lastName || "",
      ownershipPercentage: source?.ownershipPercentage || 0,
      phone: source?.phone || "",
      ssn: source?.ssn || "",
      state: source?.state || undefined,
      title: source?.title || undefined,
      zipCode: source?.zipCode || "",
    })
  }, [hydrate, source])

  // Kept apart from the prefill above, since the limit settles only once the
  // other owners load, and nothing needs refilling then. Reruns on a new
  // prefill too, which clears errors.
  useEffect(() => {
    const currentOwnership = source?.ownershipPercentage || 0
    // Set error if the initial ownership percentage exceeds the maximum for additional owners
    if (isAdditionalOwnerStep && currentOwnership > maxOwnershipPercentage) {
      form.setError("ownershipPercentage", {
        type: "manual",
        message: `Ownership percentage cannot exceed ${maxOwnershipPercentage}%
        (100% - primary owner's ${100 - maxOwnershipPercentage}%)`,
      })
    }
  }, [form, isAdditionalOwnerStep, maxOwnershipPercentage, source])

  if (isMajorityOwnerRetrieveLoading || isOwnerDraftLoading) {
    return (
      <div>
        <p>Loading...</p>
      </div>
    )
  }

  return (
    <div className="@container">
      <div className="grid grid-cols-1 lg:grid-cols-3">
        <Form {...form} key={JSON.stringify(ownershipData)}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="w-full gap-6 lg:col-span-2"
          >
            <OwnershipDetailsForm
              onAdditionalOwnerChange={setHasMultipleOwners}
              onBack={goBack}
              isAdditionalOwnerStep={isAdditionalOwnerStep}
              isSubmitting={isSubmitting}
              hasMultipleOwners={
                companyData.hasMultipleOwners || hasMultipleOwners
              }
              isCheckboxDisabled={companyData.hasMultipleOwners}
              maxOwnershipPercentage={maxOwnershipPercentage}
              autosaveStatus={autosave.status}
            />
          </form>
        </Form>
      </div>
    </div>
  )
}
